import { T } from "../styles/theme";

interface HairWashTrackerProps {
  hairLastDone: string | null;
  setHairLastDone: (next: string | null) => void;
  hairSkipped: boolean;
  setHairSkipped: (next: boolean) => void;
  hairEditOpen: boolean;
  setHairEditOpen: (next: boolean) => void;
  /** Toggle the underlying task as complete (called when user marks hair done today). */
  onMarkTaskComplete: () => void;
}

/**
 * Inline UI shown under hair-wash tasks. Tracks the last wash date,
 * a "skipped this week" state, and an edit popover.
 */
export function HairWashTracker({
  hairLastDone,
  setHairLastDone,
  hairSkipped,
  setHairSkipped,
  hairEditOpen,
  setHairEditOpen,
  onMarkTaskComplete,
}: HairWashTrackerProps) {
  return (
    <div style={{ marginTop: 5 }}>
      {hairLastDone && (
        <div
          style={{
            fontSize: 10,
            color: T.sageDark,
            background: T.sagePale,
            borderRadius: 6,
            padding: "2px 8px",
            display: "inline-block",
            marginBottom: 4,
          }}
        >
          ✓ Last washed: {hairLastDone}
        </div>
      )}
      {!hairLastDone && !hairSkipped && (
        <div style={{ fontSize: 10, color: T.inkLight, fontStyle: "italic", marginBottom: 4 }}>Not logged yet</div>
      )}
      {hairSkipped && (
        <div
          style={{
            fontSize: 10,
            color: "#c8834a",
            background: "#fff5ec",
            borderRadius: 6,
            padding: "2px 8px",
            display: "inline-block",
            marginBottom: 4,
          }}
        >
          ↩ Skipped this week
        </div>
      )}

      {!hairEditOpen && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setHairEditOpen(true);
              setHairSkipped(false);
            }}
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "3px 10px",
              background: hairLastDone ? "#eafaf0" : "#f0e8f8",
              color: hairLastDone ? T.sageDark : "#6b3fa0",
              border: `1.5px solid ${hairLastDone ? T.sage : "#6b3fa0"}44`,
              borderRadius: 20,
              cursor: "pointer",
              fontFamily: T.body,
            }}
          >
            {hairLastDone ? "Update" : "Mark done"}
          </button>
          {!hairSkipped && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setHairSkipped(true);
                setHairEditOpen(false);
              }}
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "3px 10px",
                background: "#fff5ec",
                color: "#c8834a",
                border: "1.5px solid #c8834a44",
                borderRadius: 20,
                cursor: "pointer",
                fontFamily: T.body,
              }}
            >
              Skip
            </button>
          )}
          {hairSkipped && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setHairSkipped(false);
              }}
              style={{
                fontSize: 10,
                padding: "3px 10px",
                background: T.parchment,
                color: T.inkLight,
                border: "none",
                borderRadius: 20,
                cursor: "pointer",
                fontFamily: T.body,
              }}
            >
              Undo skip
            </button>
          )}
        </div>
      )}

      {hairEditOpen && (
        <div
          style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              const d = new Date();
              setHairLastDone(d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }));
              setHairEditOpen(false);
              setHairSkipped(false);
              onMarkTaskComplete();
            }}
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "4px 12px",
              background: "#6b3fa0",
              color: "#fff",
              border: "none",
              borderRadius: 20,
              cursor: "pointer",
              fontFamily: T.body,
            }}
          >
            ✓ Mark as done today
          </button>
          <button
            onClick={() => {
              setHairLastDone(null);
              setHairEditOpen(false);
            }}
            style={{
              fontSize: 10,
              padding: "4px 10px",
              background: T.parchment,
              color: T.inkLight,
              border: "none",
              borderRadius: 20,
              cursor: "pointer",
              fontFamily: T.body,
            }}
          >
            Clear
          </button>
          <button
            onClick={() => setHairEditOpen(false)}
            style={{
              fontSize: 10,
              padding: "4px 8px",
              background: "none",
              color: T.inkLight,
              border: "none",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
